/**
 * Navigation Mode Hook
 * 
 * Manages navigation mode state: "agent" | "admin"
 * - Defaults to "agent"
 * - Admin mode only available if canAccessAdmin(user?.role)
 * - Persists in localStorage
 * - Auto-resets to "agent" if user loses admin access
 * - Uses Context to share state across all components
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { canAccessAdmin } from '../utils/roleUtils';

const STORAGE_KEY = 'parkpro.navMode';

// Create context for shared nav mode state
const NavModeContext = createContext(null);

/**
 * NavModeProvider - Provides shared navigation mode state to all children
 * Should be placed high in the component tree (e.g., in AppChrome or App)
 */
export const NavModeProvider = ({ children }) => {
  const { user } = useUser();
  const [navModeState, setNavModeState] = useState(() => {
    // Initialize from localStorage, default to "agent"
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'admin' ? 'admin' : 'agent';
    }
    return 'agent';
  });

  // Check if user can access admin
  const hasAdminAccess = canAccessAdmin(user?.role);

  // Derive effective mode: never show admin if user doesn't have access
  const navMode = (navModeState === 'admin' && !hasAdminAccess) ? 'agent' : navModeState;

  // Sync to localStorage when user loses admin access (external system only; no setState)
  useEffect(() => {
    if (navModeState === 'admin' && !hasAdminAccess && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'agent');
    }
  }, [navModeState, hasAdminAccess]);

  const setNavMode = useCallback((mode) => {
    // Only allow admin mode if user has admin access
    if (mode === 'admin' && !hasAdminAccess) {
      return;
    }
    setNavModeState(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  }, [hasAdminAccess]);

  const value = {
    navMode,
    setNavMode,
    hasAdminAccess,
    canSwitchToAdmin: hasAdminAccess,
  };

  return (
    <NavModeContext.Provider value={value}>
      {children}
    </NavModeContext.Provider>
  );
};

/**
 * Hook to access navigation mode state
 * Must be used within NavModeProvider
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook and provider live together by design
export const useNavMode = () => {
  const context = useContext(NavModeContext);
  const { user } = useUser();
  const [fallbackNavMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'admin' ? 'admin' : 'agent';
    }
    return 'agent';
  });

  if (!context) {
    // Fallback for components outside provider (backward compatibility)
    console.warn('useNavMode used outside NavModeProvider, using fallback state');
    const hasAdminAccess = canAccessAdmin(user?.role);
    return {
      navMode: fallbackNavMode,
      setNavMode: () => {},
      hasAdminAccess,
      canSwitchToAdmin: hasAdminAccess,
    };
  }
  return context;
};
